import { takeEvery } from 'redux-saga'
import { call, put, take, select, race, fork, cancel, all } from 'redux-saga/effects'
import t from 'redux/actions/actionTypes'
import { bindersSync, userSync } from 'sync'
import browserHistory from 'helpers/history'
import binderActions from 'redux/actions/binderActions'
import binder_get_lessons_selector from 'redux/selectors/binders/binder_get_lessons_selector'
import binder_get_binders_selector from 'redux/selectors/binders/binder_get_binders_selector'
import binder_search_lessons_selector from 'redux/selectors/binders/binder_search_lessons_selector'
import binder_save_lessons_selector from 'redux/selectors/binders/binder_save_lessons_selector'
import { localize } from 'helpers/translator'
import callAny from './helpers/call_any'
import overlayTypes from 'helpers/enums/overlayTypes'
import actionUserTypes from 'helpers/enums/actionUserTypes'
import formatError from './helpers/error_formatter'

function *getBindersData() {
	try {
		const binderData = yield select(binder_get_binders_selector)
		const response = yield call(bindersSync.getBindersList, binderData.page, binderData.order_field, binderData.order_type)
		if (response.error_code === 0) {
			const type = t.BINDERS_SET_ALL
			yield put({
				type,
				payload: response.payload,
			})
		} else {
			yield put({
				type: t.SYNC_UNCAUGHT_ERROR,
				payload: response.payload,
			})
		}
	} catch (error) {
		// console.log(error)
	}
}

function *doSearchLessons(searchParams) {
	try {
		const response = yield call(bindersSync.searchLessons, searchParams.entityUID, searchParams.page, searchParams.order_field, searchParams.order_type, searchParams.search)
		if (response.error_code === 0) {
			yield put({
				type: t.LESSONS_SET_ALL,
				entities: response.payload.result.entities,
			})
			yield put({
				type: t.BINDERS_FOUND_BINDER_LESSONS,
				payload: {
					entities: response.payload.result.entities,
					page: response.payload.result.page,
					more: response.payload.result.more,
				},
			})
		} else {
			throw new Error('Uncaught error')
		}
	} finally {
	}
}


function *searchLessons() {
	let currentSearchParams, currentRequest
	while (true) {
		const action = yield take([t.BINDERS_SEARCH_BINDER_LESSONS, t.BINDERS_INIT_SEARCH_BINDER_LESSONS])
		try {
			const searchParams = yield select(binder_search_lessons_selector)
			if (!(action.type !== t.BINDERS_INIT_SEARCH_BINDER_LESSONS && currentSearchParams && currentSearchParams.order_field === searchParams.order_field && currentSearchParams.order_type === searchParams.order_type && currentSearchParams.search === searchParams.search && currentSearchParams.page === searchParams.page)) {
				currentSearchParams = searchParams
				if (currentRequest) {
					yield cancel(currentRequest)
				}
				currentRequest = yield fork(doSearchLessons, searchParams)
			}
		} catch (error) {
			// console.log(error)
		}
	}
}

function *getLessons(action) {
	try {
		const binderData = yield select(binder_get_lessons_selector)
		const response = yield call(bindersSync.getLessons, binderData.entityUID, binderData.page, binderData.order_field, binderData.order_type)
		if (response.error_code === 0) {
			yield put({
				type: t.BINDERS_LESSONS_FETCHED,
				binder: response.payload.result.binder,
				entityUID: binderData.entityUID,
				entities: response.payload.result.entities,
				notes: response.payload.result.notes,
				page: response.payload.result.page,
				more: response.payload.result.more,
				limit: action.limit,
			})
		} else {
			yield put({
				type: t.SYNC_UNCAUGHT_ERROR,
				payload: response.payload,
			})
		}

	} catch (error) {
		// console.log(error)
	}
}

function *getSingleLesson(action) {
	const response = yield call(bindersSync.getLessons, action.entityUID, action.page, action.order_field, action.order_type, action.limit)
	yield put({
		type: t.BINDERS_LESSONS_FETCHED,
		entityUID: action.entityUID,
		entities: response.payload.result.entities,
		notes: response.payload.result.notes,
		page: response.payload.result.page,
		more: response.payload.result.more,
		limit: action.limit,
	})
}


function *saveNotes(action) {
	try {
		yield call(bindersSync.saveLessons, action.entityUID, [action.presentationUID])
		yield call(bindersSync.saveNotes, action.entityUID, action.presentationUID, action.notes, action.self_paceds)
		const type = t.BINDERS_SAVED_NOTES
		yield put({
			type,
			presentationUID: action.presentationUID,
			notes: action.notes,
			self_paceds: action.self_paceds,
			isLast: action.isLast,
		})
	} catch (error) {
		// console.log(error)
	}
}


function *deleteLesson(action) {
	try {
		const response = yield call(bindersSync.deleteLesson, action.entityUID, action.presentationUID)
		if (response.error_code === 0) {
			const type = t.BINDERS_DELETED_LESSON
			yield put({
				type,
				presentationUID: action.presentationUID,
				payload: response.payload,
			})
			yield *getSingleLesson(action)
		} else {
			throw new Error('Uncaught error')
		}
	} catch (error) {
		// console.log(error)
	}
}


function *updateBindersSort(action) {
	action.page = 0
	yield put({
		type: t.BINDERS_UPDATE_BINDERS_SORT,
		payload: {
			page: action.page,
			order_field: action.order_field,
			order_type: action.order_type,
		},
	})
	yield *getBindersData(action)
}


function *saveBinder(action) {
	try {
		const response = yield call(bindersSync.saveForm, action.values)
		if (response.error_code === 0) {
			yield put({
				type: t.BINDERS_SAVE_FORM_SUCCESS,
			})
		} else if (response.error_code === 23 || response.error_code === 31) { // Invalid fields
			yield put({
				type: t.BINDERS_SAVE_FORM_FAIL,
				payload: response.payload,
				error_code: response.error_code,
			})
		} else if (response.error_code === 26 || response.error_code === 1) { // Binder already Reviewed || Custom error || Older date
			yield put({
				type: t.BINDERS_SAVE_FORM_FAIL,
				payload: {},
				error_code: response.error_code,
			})
		} else {
			yield put({
				type: t.BINDERS_SAVE_FORM_UNCAUGHT,
			})
			throw new Error('Uncaught error')
		}
	} catch (error) {
		// console.log(error)
	}
}


function *shareBinder(action) {
	try {
		const response = yield call(bindersSync.shareForm, action.entityUID)
		if (response.error_code === 0) {
			yield put({
				type: t.BINDERS_SHARE_FORM_SUCCESS,
				payload: response.payload,
				entityUID: action.entityUID,
			})
		} else if (response.error_code === 24 || response.error_code === 25 || response.error_code === 30 || response.error_code === 31) {
			yield put({
				type: t.BINDERS_SHARE_FORM_FAIL,
				payload: response.payload,
				error_code: response.error_code,
			})
			let message
			switch (response.error_code) {
				case 24:
					message = localize('binders_message_requiredFields')
					break
				case 30:
					message = localize('binders_message_durationExceeded_1')
					break
				case 31:
					message = localize('binders_message_priorToToday')
					break
				default:
					message = localize('binders_message_requiredFields')
					break
			}
			yield put({
				type: t.OPEN_TOP_INFORMATION,
				messageObject: {
					message,
					timeout: 3000,
					styleType: 'error',
				},
			})
		} else {
			throw new Error('Uncaught error')
		}
	} catch (error) {
		// console.log(error)
	}
}

function *deleteBinder(action) {
	try {
		const response = yield call(bindersSync.deleteBinder, action.entityUID)
		if (response.error_code === 0) {
			yield put({
				type: t.BINDERS_DELETED_BINDER,
				payload: response.payload,
				entityUID: action.entityUID,
			})
			browserHistory.push('/binders/list')
		} else {
			throw new Error('Uncaught error')
		}
	} catch (error) {
		// console.log(error)
	}
}

function *shareBinderFromLessons(action) {
	yield put({
		...action,
		type: t.BINDERS_SHARE_FORM,
	})
	const result = yield take([t.BINDERS_SHARE_FORM_SUCCESS, t.BINDERS_SHARE_FORM_FAIL])
	if (result.type === t.BINDERS_SHARE_FORM_FAIL) {
		yield put({
			errors: result.payload.errors,
			type: t.BINDERS_STORE_ERRORS,
		})
		browserHistory.push('/binders/' + action.entityUID + '/form?ds=1')
	} else {
		yield put({
			type: t.OPEN_TOP_INFORMATION,
			messageObject: {
				message: localize('binders_message_subPlanSent'),
				timeout: 3000,
				styleType: 'success',
			},
		})
		browserHistory.push('/binders/list')
	}
}

function *saveAndShareBinder(action) {
	try {
		yield put({
			...action,
			type: t.BINDERS_SAVE_FORM,
		})
		const result = yield take([t.BINDERS_SAVE_FORM_SUCCESS, t.BINDERS_SAVE_FORM_FAIL, t.BINDERS_SAVE_FORM_UNCAUGHT])
		if (result.type === t.BINDERS_SAVE_FORM_SUCCESS) {
			if (action.share) {
				yield put({
					entityUID: action.values.entityUID,
					type: t.BINDERS_SHARE_FORM,
				})
				const binderResult = yield take([t.BINDERS_SHARE_FORM_SUCCESS, t.BINDERS_SHARE_FORM_FAIL, t.BINDERS_SHARE_FORM_UNCAUGHT])
				if (binderResult.type === t.BINDERS_SHARE_FORM_SUCCESS) {
					action.resolve()
					yield put({
						type: t.OPEN_TOP_INFORMATION,
						messageObject: {
							message: localize('binders_message_subSent'),
							timeout: 4000,
							styleType: 'success',
						},
					})
					browserHistory.push('/binders/list')
				} else if (binderResult.type === t.BINDERS_SHARE_FORM_FAIL) {
					const errors = binderResult.payload.errors.reduce(function(accumulator, currentItem) {
						accumulator[currentItem.id] = currentItem.message
						return accumulator
					}, {})
					action.reject({
						_error: result.message,
						...errors,
					})
				} else {
					action.reject()
				}
			} else {
				action.resolve()
				yield put({
					type: t.OPEN_TOP_INFORMATION,
					messageObject: {
						message: localize('binders_message_subPlanSaved'),
						timeout: 3000,
						styleType: 'success',
					},
				})
				//browserHistory.push('/binders/' + action.values.entityUID + '/lessons')
				browserHistory.push('/binders/list')
			}
		} else if (result.type === t.BINDERS_SAVE_FORM_FAIL) {
			const errors = result.payload.errors.reduce(function(accumulator, currentItem) {
				accumulator[currentItem.id] = currentItem.message
				return accumulator
			}, {})
			action.reject({
				_error: localize('binders_message_error'),
				...errors,
			})
		} else {
			action.reject()
		}

	} catch (error) {
		// console.log(error)
	}
}

function *saveLessons() {
	try {
		const searchParams = yield select(binder_save_lessons_selector)
		const response = yield call(bindersSync.saveLessons, searchParams.entityUID, searchParams.presentationUIDs)
		if (response.error_code === 0) {
			yield put({
				type: t.BINDERS_SAVE_LESSONS_SUCCESS,
				presentationUIDs: searchParams.presentationUIDs,
			})
		} else {
			throw new Error('Uncaught error')
		}
	} catch (error) {
		// console.log(error)
	}
}

function *createBinder(action) {
	const response = yield call(bindersSync.createBinder, action.name)
	if (response.error_code === 0) {
		yield put({
			type: t.BINDERS_BINDER_CREATE_NEW,
			payload: response.payload,
		})
		browserHistory.push('/binders/' + response.payload.form_data.entityUID + '/lessons')

	} else if (response.error_code === 23) {
		yield put({
			type: t.BINDERS_BINDER_CREATE_FAIL,
			error: response.payload.errors[0].message,

		})
		//TODO display inline error
	} else {
		throw new Error('Uncaught error')
	}
}

function *getBinderInformation(action) {
	try {
		const response = yield call(bindersSync.getBinderInformation, action.entityUID)
		if (response.error_code === 0) {
			yield put({
				type: t.BINDERS_SET_BINDER_DATA,
				binder: response.payload,
				entityUID: action.entityUID,
			})
		} else {
			yield put({
				type: t.SYNC_UNCAUGHT_ERROR,
				payload: response.payload,
			})
		}
	} catch (error) {
		// console.log(error)
	}
}

function *uploadDocument(action) {
	try {
		const response = yield call(bindersSync.uploadDocument, action.entityUID, action.file)
		if (response.error_code === 0) {
			yield put({
				type: t.BINDERS_UPLOAD_DOCUMENT_SUCCESS,
				entityUID: action.entityUID,
				name: action.file.name,
			})
			yield put({
				type: t.OPEN_TOP_INFORMATION,
				messageObject: {
					message: response.message,
					timeout: 3000,
					styleType: 'success',
				},
			})
		} else if (response.error_code === 1) {
			yield put({
				type: t.BINDERS_UPLOAD_DOCUMENT_FAIL,
			})
			yield put({
				type: t.OPEN_TOP_INFORMATION,
				messageObject: {
					message: response.message,
					timeout: 3000,
					styleType: 'error',
				},
			})
		} else {
			yield put({
				type: t.SYNC_UNCAUGHT_ERROR,
				payload: response.payload,
			})
		}
	} catch (error) {
		// console.log(error)
	}
}

function *searchLessonsCode(action) {
	const result = yield race({
		lessons: call(bindersSync.getLessonCodes, action.entityUID, action.presentationUID),
		close: take(t.BINDERS_CLOSE_CODES_MODAL),
	})
	if (result.lessons) {
		yield put({
			type: t.BINDERS_LESSON_CODES_SUCCESS,
			dateFormat: result.lessons.payload.dateFormat,
			timeFormat: result.lessons.payload.timeFormat,
			codes: result.lessons.payload.activePins,
			actualTimestamp: result.lessons.payload.actualTimestamp,
		})
	}
}

function *onboardingStep(action) {
	try {
		if (action.step === 2) {
			yield all([
				callAny(bindersSync.sendDefaultSubplan),
				callAny(bindersSync.addSubsLessons),
				callAny(userSync.loginNotification,
					overlayTypes.FEATURE_BINDERS_WELCOME_OVERLAY_ID,
					null,
					actionUserTypes.ACTION_CONTINUE
				),
			])
		}
	} catch (error) {
		const serverError = formatError(error)
		yield put(binderActions.onboardingFailed(serverError))
	}
}

export default [
	takeEvery(t.BINDERS_GET_ALL, getBindersData),
	fork(searchLessons),
	takeEvery([t.BINDERS_GET_BINDER_LESSONS, t.BINDERS_SAVED_NOTES], getLessons),
	takeEvery(t.BINDERS_GET_BINDER_INFORMATION, getBinderInformation),
	takeEvery(t.BINDERS_DELETE_LESSON, deleteLesson),
	takeEvery(t.BINDERS_DELETE_BINDER, deleteBinder),
	takeEvery(t.BINDERS_SAVE_NOTES, saveNotes),
	takeEvery(t.BINDERS_SORT_BINDERS, updateBindersSort),
	takeEvery(t.BINDERS_SAVE_FORM, saveBinder),
	takeEvery(t.BINDERS_SHARE_FORM, shareBinder),
	takeEvery(t.BINDERS_SHARE_FORM_FROM_LESSONS, shareBinderFromLessons),
	takeEvery(t.BINDERS_SAVE_SHARE_FORM, saveAndShareBinder),
	takeEvery(t.BINDERS_SAVE_LESSONS, saveLessons),
	takeEvery(t.BINDERS_BINDER_CREATE, createBinder),
	takeEvery(t.BINDERS_UPLOAD_DOCUMENT, uploadDocument),
	takeEvery(t.BINDERS_SHOW_LESSONS_CODE, searchLessonsCode),
	takeEvery(t.BINDERS_ONBOARDING_GO_TO_STEP, onboardingStep),
]

export {
	onboardingStep,
}
